import { FetchArgs, FetchBaseQueryError, QueryReturnValue } from "@reduxjs/toolkit/query";

import { PagedResponseDto } from "../dtos/paged-response.dto";

import type { ExecuteBaseQuery } from "./create-query-fn";

const DEFAULT_FETCH_ALL_PAGE_SIZE = 10000;
const INVALID_PAGED_RESPONSE_ERROR: FetchBaseQueryError = {
  status: "CUSTOM_ERROR",
  error: "Invalid paged response",
};

function isPagedResponseDto<TDto>(value: unknown): value is PagedResponseDto<TDto> {
  if (typeof value !== "object" || value === null) {
    return false;
  }

  if (!("data" in value) || !Array.isArray(value.data)) {
    return false;
  }

  if (!("meta" in value)) {
    return false;
  }

  const meta = value.meta;
  if (typeof meta !== "object" || meta === null) {
    return false;
  }

  return "totalCount" in meta && typeof meta.totalCount === "number";
}

function getTotalPages<TDto>(response: PagedResponseDto<TDto>, pageSize: number): number {
  return response.meta.totalPages ?? Math.ceil(response.meta.totalCount / pageSize);
}

export async function rtkqFetchAllPages<TDto, TResult>(
  executeBaseQuery: ExecuteBaseQuery,
  request: FetchArgs,
  transformData: (data: TDto[]) => TResult,
  pageSize: number = DEFAULT_FETCH_ALL_PAGE_SIZE
): Promise<QueryReturnValue<TResult, FetchBaseQueryError, undefined>> {
  const data: TDto[] = [];
  let pageNumber = 1;
  let totalPages = 1;

  while (pageNumber <= totalPages) {
    const response = await executeBaseQuery({
      ...request,
      params: {
        ...request.params,
        pageNumber,
        pageSize,
      },
    });

    if (response.error !== undefined) {
      return { error: response.error };
    }

    if (!isPagedResponseDto<TDto>(response.data)) {
      return { error: INVALID_PAGED_RESPONSE_ERROR };
    }

    data.push(...response.data.data);
    totalPages = getTotalPages(response.data, pageSize);
    pageNumber += 1;
  }

  return { data: transformData(data) };
}
